import { useState } from "react";
import { Link } from "wouter";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { SCIENCE_LLMS } from "@/lib/data";
import {
  Search, FlaskConical, ArrowRight, ExternalLink, Zap,
  MessageSquare, FileText, Cpu, Star, ChevronRight,
  Copy, Check, Filter, Microscope, Atom, Beaker,
  Database, Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTheme } from "@/contexts/ThemeContext";

const CAPABILITIES_ICONS: Record<string, React.ReactNode> = {
  "Molecular Property Prediction": <Cpu size={12} />,
  "Reaction Prediction": <FlaskConical size={12} />,
  "Literature Analysis": <FileText size={12} />,
  "Hypothesis Generation": <MessageSquare size={12} />,
  "ADMET Reasoning": <Zap size={12} />,
};

function FeaturedModelCard({ model }: { model: typeof SCIENCE_LLMS[0] }) {
  const [copied, setCopied] = useState(false);
  const { language } = useLanguage();
  const { platformTheme } = useTheme();
  const isDark = platformTheme === "tech";
  const isFlag = model.tier === "Flagship";

  const handleCopy = () => {
    navigator.clipboard.writeText(model.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={cn(
      "sci-card p-8 transition-all duration-300 relative overflow-hidden",
      isFlag
        ? "border-blue-500/30 hover:border-blue-500/50 sci-glow"
        : "border-cyan-500/20 hover:border-cyan-500/40",
      isDark ? "" : "bg-white"
    )}>
      {isFlag && (
        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/5 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      )}
      <div className="relative">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={cn(
              "w-14 h-14 rounded-xl flex items-center justify-center text-base font-bold border",
              isFlag
                ? "bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400"
                : "bg-gradient-to-br from-cyan-500/15 to-emerald-500/15 border-cyan-500/25 text-cyan-400"
            )}>
              {model.providerShort}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3
                  className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-900")}
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  {model.name}
                </h3>
                {isFlag && <Star size={16} className="text-amber-400 fill-amber-400" />}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">{model.provider}</p>
            </div>
          </div>
          <span className={cn(
            "text-[10px] px-2.5 py-1 rounded-full border font-medium",
            isFlag
              ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
              : "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"
          )}>
            {model.tier}
          </span>
        </div>

        <p className={cn("text-sm leading-relaxed mb-5", isDark ? "text-slate-300" : "text-slate-600")}>
          {language === "en" ? model.descriptionEn : model.description}
        </p>

        <div className="flex flex-wrap gap-1.5 mb-4">
          {model.tags.map((tag) => (
            <span key={tag} className="tag-llm text-[10px] px-2 py-0.5 rounded-full font-medium">
              {tag}
            </span>
          ))}
        </div>

        <div className="flex flex-wrap gap-1.5 mb-5">
          {model.capabilities.map((cap) => (
            <span key={cap} className="flex items-center gap-1 text-[10px] text-slate-500 bg-white/3 border border-white/8 px-2 py-0.5 rounded-full">
              {CAPABILITIES_ICONS[cap]}
              {cap}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <div className={cn(
            "flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border",
            isDark ? "bg-[#0D1117] border-white/8" : "bg-slate-50 border-slate-200"
          )}>
            <code className={cn("text-xs font-mono truncate", isDark ? "text-slate-400" : "text-slate-500")}>{model.id}</code>
            <button onClick={handleCopy} className="flex-shrink-0 text-slate-600 hover:text-slate-300 transition-colors">
              {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
            </button>
          </div>
          <Link href="/console/llms">
            <Button className="btn-primary-sci text-xs px-5 h-9 gap-1.5">
              {language === "en" ? "Try Now" : "立刻体验"} <ArrowRight size={12} />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function ProprietaryCard({ model }: { model: typeof SCIENCE_LLMS[0] }) {
  const [copied, setCopied] = useState(false);
  const { language } = useLanguage();
  const { platformTheme } = useTheme();
  const isDark = platformTheme === "tech";

  const handleCopy = () => {
    navigator.clipboard.writeText(model.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const domainIcons: Record<string, React.ReactNode> = {
    "XtalFold™": <Microscope size={16} className="text-blue-400" />,
    "XFEP": <Atom size={16} className="text-cyan-400" />,
    "XMolGen": <Beaker size={16} className="text-emerald-400" />,
    "XtalGazer™ CSP": <Database size={16} className="text-violet-400" />,
    "Reaction Predictor": <FlaskConical size={16} className="text-amber-400" />,
    "ADMET Predictor": <Activity size={16} className="text-rose-400" />,
    "Materials Predictor": <Zap size={16} className="text-orange-400" />,
  };

  return (
    <div className={cn(
      "sci-card p-5 hover:border-slate-400/30 transition-all duration-300",
      isDark ? "border-white/8" : "bg-white border-slate-200"
    )}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className={cn(
            "w-9 h-9 rounded-lg flex items-center justify-center",
            isDark ? "bg-white/5" : "bg-slate-50"
          )}>
            {domainIcons[model.name] || <Cpu size={16} className="text-slate-400" />}
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <h3
                className={cn("text-sm font-bold", isDark ? "text-white" : "text-slate-900")}
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {model.name}
              </h3>
              {model.isNew && (
                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 font-semibold">NEW</span>
              )}
            </div>
            <p className="text-[10px] text-slate-500">{model.provider}</p>
          </div>
        </div>
        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-500/10 text-slate-500 border border-slate-500/15 font-medium">
          {language === "en" ? "Foundation" : "基座算法"}
        </span>
      </div>

      <p className={cn("text-xs leading-relaxed mb-3 line-clamp-3", isDark ? "text-slate-400" : "text-slate-600")}>
        {language === "en" ? model.descriptionEn : model.description}
      </p>

      <div className="flex flex-wrap gap-1 mb-3">
        {model.tags.slice(0, 3).map((tag) => (
          <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 border border-white/8 text-slate-500">
            {tag}
          </span>
        ))}
        {model.tags.length > 3 && (
          <span className="text-[9px] px-1.5 py-0.5 text-slate-600">+{model.tags.length - 3}</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        <div className={cn(
          "flex-1 flex items-center gap-2 px-2 py-1.5 rounded border",
          isDark ? "bg-[#0D1117] border-white/8" : "bg-slate-50 border-slate-200"
        )}>
          <code className={cn("text-[10px] font-mono truncate", isDark ? "text-slate-500" : "text-slate-400")}>{model.id}</code>
          <button onClick={handleCopy} className="flex-shrink-0 text-slate-600 hover:text-slate-300 transition-colors">
            {copied ? <Check size={10} className="text-emerald-400" /> : <Copy size={10} />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ScienceLLMs() {
  const [search, setSearch] = useState("");
  const { language, t } = useLanguage();
  const { platformTheme } = useTheme();
  const isDark = platformTheme === "tech";

  const scienceLLMs = SCIENCE_LLMS.filter(m => m.tier === "Flagship" || m.tier === "Standard");
  const proprietaryModels = SCIENCE_LLMS.filter(m => m.tier === "Proprietary");

  const filteredProprietary = proprietaryModels.filter((m) => {
    if (!search) return true;
    return m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.description.toLowerCase().includes(search.toLowerCase()) ||
      m.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
  });

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-foreground">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-16 sci-bg-grid relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/5 to-transparent" />
        <div className="relative max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-xs text-slate-500 mb-6">
            <Link href="/" className="hover:text-slate-300">Home</Link>
            <ChevronRight size={12} />
            <span className={isDark ? "text-slate-300" : "text-slate-700"}>Science LLMs</span>
          </div>

          <div className="flex items-start justify-between gap-8 flex-wrap">
            <div className="max-w-[600px]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                  <FlaskConical size={20} className="text-blue-400" />
                </div>
                <h1
                  className={cn("text-4xl font-bold", isDark ? "text-white" : "text-slate-900")}
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  Science LLMs
                </h1>
              </div>
              <p className={cn("text-base leading-relaxed mb-3", isDark ? "text-slate-300" : "text-slate-600")}>
                {language === "en"
                  ? "XtalPi Science Flagship and Standard are the models you call directly — natively understanding SMILES, FASTA, InChI and more. Behind them, 200+ proprietary algorithms form the computational foundation."
                  : "XtalPi Science Flagship 和 Standard 是您直接调用的模型——原生理解 SMILES、FASTA、InChI 等科学格式。它们背后有 200+ 自研专有算法作为计算基座。"}
              </p>
              <p className={cn("text-sm mb-6", isDark ? "text-slate-500" : "text-slate-500")}>
                {language === "en"
                  ? "Science LLM as the interface. Proprietary algorithms as the engine."
                  : "Science LLM 是接口，专有算法是引擎。"}
              </p>
              <div className="flex flex-wrap gap-3">
                {[
                  { label: "Anthropic Compatible", color: "text-blue-400 bg-blue-500/10 border-blue-500/20" },
                  { label: "SMILES / FASTA Native", color: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20" },
                  { label: "Streaming Support", color: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20" },
                ].map((tag) => (
                  <span key={tag.label} className={cn("text-xs px-3 py-1 rounded-full border font-medium", tag.color)}>
                    {tag.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex-1 min-w-[300px] max-w-[440px]">
              <div className="code-block">
                <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
                  <span className="text-xs text-slate-500">Quick Start</span>
                  <span className="text-[10px] text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-full">Python</span>
                </div>
                <pre className="p-4 text-xs font-mono text-slate-300 overflow-x-auto leading-relaxed">
{`client = anthropic.Anthropic(
  api_key="sk-st-...",
  base_url=os.environ["SCIENCETOKEN_BASEURL"]
)

message = client.messages.create(
  model="xtalpi-science-standard",
  max_tokens=1024,
  messages=[{
    "role": "user",
    "content": "Predict logP for SMILES: ..."
  }]
)`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Science LLM: Primary Models ── */}
      <section className="py-12">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Badge className="mb-3 bg-blue-500/10 text-blue-400 border-blue-500/20 text-xs">
              {language === "en" ? "User-Facing Models" : "用户直接调用"}
            </Badge>
            <h2
              className={cn("text-2xl sm:text-3xl font-bold mb-2", isDark ? "text-white" : "text-slate-900")}
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              {language === "en" ? "Science LLM — Call Directly via API" : "Science LLM — 通过 API 直接调用"}
            </h2>
            <p className={cn("text-sm max-w-[600px]", isDark ? "text-slate-400" : "text-slate-600")}>
              {language === "en"
                ? "These are the models you interact with. They integrate 200+ proprietary algorithms internally, delivering specialized scientific capabilities through a simple chat interface."
                : "这是您直接交互的模型。它们在内部整合了 200+ 自研专有算法，通过简单的对话接口交付专业科学能力。"}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {scienceLLMs.map((model) => (
              <FeaturedModelCard key={model.id} model={model} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Connecting visual ── */}
      <div className="flex flex-col items-center py-4">
        <div className={cn("w-px h-8", isDark ? "bg-gradient-to-b from-blue-500/30 to-white/10" : "bg-gradient-to-b from-blue-400/30 to-slate-200")} />
        <div className={cn(
          "px-4 py-2 rounded-full border text-xs font-medium",
          isDark ? "bg-white/5 border-white/10 text-slate-400" : "bg-slate-50 border-slate-200 text-slate-500"
        )}>
          {language === "en" ? "Powered by" : "强大基座支撑"}
        </div>
        <div className={cn("w-px h-8", isDark ? "bg-gradient-to-b from-white/10 to-slate-500/20" : "bg-gradient-to-b from-slate-200 to-slate-100")} />
      </div>

      {/* ── Proprietary Algorithm Foundation ── */}
      <section className="py-12 bg-[var(--page-bg-alt)]">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
            <div>
              <Badge className="mb-3 bg-slate-500/10 text-slate-400 border-slate-500/20 text-xs">
                {language === "en" ? "Foundation Layer" : "算法基座"}
              </Badge>
              <h2
                className={cn("text-2xl sm:text-3xl font-bold mb-2", isDark ? "text-white" : "text-slate-900")}
                style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              >
                {language === "en" ? "Proprietary Algorithm Foundation" : "自研专有算法基座"}
              </h2>
              <p className={cn("text-sm max-w-[560px]", isDark ? "text-slate-500" : "text-slate-500")}>
                {language === "en"
                  ? "These specialized algorithms power every Science LLM response. They run behind the scenes — you don't call them directly, but they make the science happen."
                  : "这些专有算法为每一次 Science LLM 响应提供计算支撑。它们在幕后运行——您无需直接调用，但正是它们让科学成为可能。"}
              </p>
            </div>
            <div className="relative min-w-[200px] max-w-[300px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <Input
                placeholder={language === "en" ? "Search algorithms..." : "搜索算法..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={cn(
                  "pl-9 text-sm h-9",
                  isDark
                    ? "bg-white/5 border-white/10 text-white placeholder:text-slate-600"
                    : "bg-white border-slate-200 text-slate-900 placeholder:text-slate-400"
                )}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProprietary.map((model) => (
              <ProprietaryCard key={model.id} model={model} />
            ))}

            {/* More models card */}
            {!search && (
              <div className={cn(
                "sci-card p-5 transition-all duration-300 border-dashed flex flex-col items-center justify-center min-h-[180px] cursor-default",
                isDark ? "border-white/10" : "border-slate-200"
              )}>
                <div className={cn(
                  "w-14 h-14 rounded-xl flex items-center justify-center mb-4",
                  isDark ? "bg-white/5" : "bg-slate-50"
                )}>
                  <span className={cn("text-2xl font-black", isDark ? "text-slate-500" : "text-slate-400")} style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    200+
                  </span>
                </div>
                <p className={cn("text-sm font-semibold mb-1", isDark ? "text-slate-400" : "text-slate-500")}>
                  {language === "en" ? "More Models" : "更多模型"}
                </p>
                <p className={cn("text-xs text-center max-w-[200px]", isDark ? "text-slate-600" : "text-slate-400")}>
                  {language === "en"
                    ? "200+ proprietary algorithms are being progressively tokenized and will be available soon."
                    : "200+ 自研专有算法正在逐步 Token 化，即将陆续上线"}
                </p>
                <span className="mt-3 text-[9px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 font-semibold">
                  {language === "en" ? "Coming Soon" : "即将上线"}
                </span>
              </div>
            )}
          </div>
          {filteredProprietary.length === 0 && (
            <div className={cn("text-center py-16", isDark ? "text-slate-500" : "text-slate-400")}>
              {t("llms.noResults")}
            </div>
          )}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className={cn("text-2xl font-bold mb-8", isDark ? "text-white" : "text-slate-900")}
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            {t("llms.comparison")}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={cn("border-b", isDark ? "border-white/10" : "border-slate-200")}>
                  <th className="text-left py-3 px-4 text-xs text-slate-500 font-medium">Model</th>
                  <th className="text-left py-3 px-4 text-xs text-slate-500 font-medium">Tier</th>
                  <th className="text-left py-3 px-4 text-xs text-slate-500 font-medium">{t("llms.bestFor")}</th>
                </tr>
              </thead>
              <tbody>
                {scienceLLMs.map((model) => (
                  <tr key={model.id} className={cn(
                    "border-b transition-colors",
                    isDark ? "border-white/5 hover:bg-white/3 bg-blue-500/5" : "border-slate-100 hover:bg-slate-50 bg-blue-50/50"
                  )}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <span className={cn("font-medium", isDark ? "text-white" : "text-slate-900")}>{model.name}</span>
                        {model.isFeatured && <Star size={11} className="text-amber-400 fill-amber-400" />}
                      </div>
                      <div className="text-xs text-slate-500">{model.provider}</div>
                    </td>
                    <td className="py-3 px-4">
                      <span className={cn(
                        "text-[10px] px-2 py-0.5 rounded-full border font-medium",
                        model.tier === "Flagship"
                          ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                          : "bg-cyan-500/15 text-cyan-400 border-cyan-500/30"
                      )}>
                        {model.tier}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-400">{model.capabilities[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
